import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Clock } from "lucide-react";
import usePopoverPlacement from "./usePopoverPlacement";

/**
 * DS TimeInput — figma 1581:48673 "Picker / button-time picker".
 *
 * native `<input type="time">` 을 쓰지 않는다 — Safari 데스크톱은 time 입력에
 * 네이티브 피커 팝업이 없어(showPicker 미지원) 클릭해도 아무것도 뜨지 않는다.
 * 트리거 버튼 클릭 시 DS 팝오버(.ds-dropdown-popover--time) 목록을 열어
 * 브라우저와 무관하게 고른다. DSDateInput 은 여전히 native date 오버레이 패턴.
 *
 * 목록 계약:
 *  - min~max(없으면 00:00~23:50) 안의 10분 격자 "HH:mm". 범위 밖 항목은 뺀다.
 *  - 현재 value 가 격자에 없어도 범위 안이면(옛 저장분 14:05) 제자리에 끼워 선택 표시.
 *    범위 밖 저장값은 트리거에 표기만 하고 목록엔 두지 않는다 — 목록에 있으면 다시 골라
 *    발송창 밖 시각이 onChange 로 나간다.
 *  - 선택 시 onChange("HH:mm") 후 닫힘. 바깥 클릭·Escape 로 닫힘.
 *
 * @param {object} props
 * @param {string} props.value - "HH:mm" (빈 문자열이면 미선택)
 * @param {(value: string) => void} props.onChange
 * @param {string} [props.min] - "HH:mm"
 * @param {string} [props.max] - "HH:mm"
 * @param {boolean} [props.disabled]
 * @param {string} [props.placeholder="시간 선택"]
 */
const STEP_MINUTES = 10;
const DAY_MINUTES = 24 * 60;

// "HH:mm" → 분. 형식이 아니면 null (min/max 가 비었거나 잘못돼도 격자 전체로 동작)
const toMinutes = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h > 23 || mm > 59) return null;
  return h * 60 + mm;
};

const pad2 = (n) => String(n).padStart(2, "0");
const toLabel = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;

// 격자는 항상 00:00 기준 10분 눈금 — min 이 08:05 여도 08:10, 08:20… 으로 맞춘다
const buildOptions = (value, min, max) => {
  const lo = toMinutes(min);
  const hi = toMinutes(max);
  const from = lo === null ? 0 : lo;
  const to = hi === null ? DAY_MINUTES - STEP_MINUTES : hi;
  const ticks = [];
  for (let t = 0; t < DAY_MINUTES; t += STEP_MINUTES) {
    if (t >= from && t <= to) ticks.push(t);
  }
  const cur = toMinutes(value);
  if (cur !== null && cur >= from && cur <= to && !ticks.includes(cur)) {
    ticks.push(cur);
    ticks.sort((a, b) => a - b);
  }
  return ticks.map(toLabel);
};

const DSTimeInput = ({
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder = "시간 선택",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const selectedRef = useRef(null);
  const popoverStyle = usePopoverPlacement(open, wrapperRef);

  const options = useMemo(() => buildOptions(value, min, max), [value, min, max]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const focusTrigger = () => {
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      focusTrigger();
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // 열릴 때 선택 항목이 보이도록 목록(.ds-dropdown-popover, 항목의 offsetParent) 의
  // scrollTop 만 맞춘다 — scrollIntoView 금지. scrollIntoView 는 조상 스크롤러(.cpe-page·
  // 모달 본문·window)까지 굴리는데, 배치 style 은 usePopoverPlacement 의 layout effect 가
  // setState 한 뒤 두 번째 커밋에서야 붙으므로 첫 커밋엔 팝오버가 CSS 기본(아래·360)으로
  // 뷰포트 밖까지 삐져나가 있고, 그 상태에서 항목을 보이려 페이지가 튀어 오른다.
  // 배치 style 이 붙어 max-height 가 줄면 먼저 맞춘 위치가 어긋나므로 그때도 다시 맞춘다.
  useLayoutEffect(() => {
    if (!open) return;
    const el = selectedRef.current;
    const list = el && el.parentElement;
    if (!list) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [open, popoverStyle]);

  const handleSelect = (next) => {
    if (onChange) onChange(next);
    setOpen(false);
    focusTrigger();
  };

  return (
    <div
      ref={wrapperRef}
      className={[
        "ds-time-input",
        open && "ds-time-input--open",
        disabled && "ds-time-input--disabled",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        className="ds-time-input__trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((p) => !p)}
      >
        <Clock className="ds-time-input__icon" />
        <span
          className={[
            "ds-time-input__text",
            value && "ds-time-input__text--filled",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {value || placeholder}
        </span>
      </button>
      {open && (
        <div
          className="ds-dropdown-popover ds-dropdown-popover--time"
          role="listbox"
          style={popoverStyle || undefined}
        >
          {options.length === 0 ? (
            <div className="ds-dropdown-popover__empty">선택할 수 있는 시간이 없어요.</div>
          ) : (
            options.map((t) => {
              const isSelected = t === value;
              return (
                <button
                  key={t}
                  ref={isSelected ? selectedRef : null}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={[
                    "ds-dropdown-popover__item",
                    isSelected && "ds-dropdown-popover__item--selected",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleSelect(t)}
                >
                  {t}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(DSTimeInput);
